import { resolveManagedChatModel, resolveManagedModel } from "../environment";
import { useQuery, useZero } from "@rocicorp/zero/react";
import {
  type AIProviderId,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../shared/aiProviders";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useManagedAIProvider } from "./useManagedAIProvider";

export type ModelScope =
  | { type: "branch"; branchId: string }
  | { type: "personal" };

// Resolves the model a given chat scope will actually run with, for any user. A
// scope follows the tenant default until the user pins a choice: their stored
// id, the managed sentinel, or — when the pinned config no longer exists or is
// unavailable to them — back to the default.
export function useScopedModel(
  scope: ModelScope,
  options?: {
    enabled?: boolean;
    chatModel?: string | null;
  }
) {
  const { enabled = true, chatModel: chatModelOverride } = options ?? {};
  const z = useZero();
  const [configsResult] = useQuery(
    enabled ? queries.availableAIProviders() : undefined
  );
  const configs = configsResult ?? [];
  const [preferences, preferencesResult] = useQuery(queries.userPreferences());
  const [branchPref, branchPrefResult] = useQuery(
    enabled
      ? queries.branchModelPreference({
          branchId: scope.type === "branch" ? scope.branchId : "",
        })
      : undefined
  );

  const chatModel =
    chatModelOverride ??
    (scope.type === "branch" ? branchPref?.chatModel : null) ??
    preferences?.chatModel ??
    null;

  const {
    availableProvider: managedProvider,
    managedChatModel: tenantManagedChatModel,
    managedEffort,
  } = useManagedAIProvider();
  const managedDefaultChatModel = resolveManagedChatModel(
    tenantManagedChatModel
  );
  const activeConfig = configs.find((c) => c.active) ?? null;
  const tenantDefaultKey: string | null = activeConfig
    ? activeConfig.id
    : managedProvider
      ? MANAGED_AI_PROVIDER_ID
      : null;

  const prefValue =
    (scope.type === "branch"
      ? branchPref?.aiProviderId
      : preferences?.aiProviderId) ?? null;

  const effectiveKey: string | null =
    prefValue === MANAGED_AI_PROVIDER_ID
      ? managedProvider
        ? MANAGED_AI_PROVIDER_ID
        : tenantDefaultKey
      : prefValue &&
          configs.some((c) => c.id === prefValue && c.disabledAt == null)
        ? prefValue
        : tenantDefaultKey;

  const effectiveConfig =
    effectiveKey && effectiveKey !== MANAGED_AI_PROVIDER_ID
      ? (configs.find((c) => c.id === effectiveKey) ?? null)
      : null;

  const providerId: AIProviderId = effectiveConfig
    ? resolveAIProviderId(effectiveConfig)
    : effectiveKey === MANAGED_AI_PROVIDER_ID && managedProvider
      ? resolveManagedModel(chatModel ?? managedDefaultChatModel).provider
      : (managedProvider ?? resolveAIProviderId());

  const setModel = (
    aiProviderId: string | null,
    managedChatModel: string | null = null
  ) => {
    if (scope.type === "branch") {
      z.mutate(
        sharedMutators.setBranchModelPreference({
          branchId: scope.branchId,
          aiProviderId,
          chatModel: managedChatModel,
        })
      );
    } else {
      z.mutate(
        sharedMutators.setUserPreference({
          aiProviderId,
          chatModel: managedChatModel,
        })
      );
    }
  };

  return {
    managedProvider,
    configs,
    tenantDefaultKey,
    effectiveKey,
    effectiveConfig,
    providerId,
    chatModel,
    chatModelReady:
      preferencesResult.type === "complete" &&
      (scope.type === "personal" || branchPrefResult.type === "complete"),
    managedDefaultChatModel,
    managedEffort,
    setModel,
  };
}

export type ScopedModel = ReturnType<typeof useScopedModel>;

// The tenant default provider, for contexts with no per-scope model (e.g.
// starting a new workflow). Callers pass the result to useFileDropzone.
export function useTenantDefaultProviderId(options?: {
  enabled?: boolean;
  chatModel?: string | null;
}): AIProviderId {
  const { enabled = true, chatModel } = options ?? {};
  const { availableProvider, managedChatModel } = useManagedAIProvider();
  const [activeProvider] = useQuery(
    enabled ? queries.activeAIProvider() : undefined
  );
  return activeProvider
    ? resolveAIProviderId(activeProvider)
    : availableProvider
      ? resolveManagedModel(chatModel ?? managedChatModel).provider
      : "anthropic";
}
